import { CheckCircle, TriangleAlert } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useAuthStore, useAuthActions } from '../../stores/authStore';
import { updateUserProfile, UserProfileUpdate, uploadUserAvatar } from '../../services/userService';

import { Button } from '../Common/button';
import { useTranslation } from 'react-i18next';


const UserProfile: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const { updateUser: updateUserInStore } = useAuthActions();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());

  // Initialize state from user
  useEffect(() => {
    if (user) {
      setName(user.name || '');
    }
  }, [user]);

  const handleSaveChanges = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const updateData: UserProfileUpdate = {};
    if (name !== (user?.name || '')) {
      updateData.name = name;
    }

    if (Object.keys(updateData).length === 0) {
      setSuccess(t('userProfile.noChanges', 'Aucune modification détectée.'));
      setIsLoading(false);
      return;
    }

    try {
      const updatedUser = await updateUserProfile(updateData);
      updateUserInStore(updatedUser);
      setSuccess(t('userProfile.success', 'Profil mis à jour avec succès !'));
    } catch (err: unknown) {
      console.error('Erreur lors de la mise à jour du profil:', err);
      const errorMessage = err instanceof Error ? err.message : t('global.somethingWentWrong');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };





  const inputClasses = "mt-1 block w-full px-3 py-2 rounded-md border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-sm disabled:bg-muted";


  if (!user) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        {t('userProfile.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Section */}
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
          {t('navbar.profile', 'Mon Profil')}
        </h3>

        {error && (
          <div className="mb-4 bg-red-100/20 border border-destructive text-destructive px-4 py-3 rounded relative flex items-center" role="alert">
            <TriangleAlert className="h-5 w-5 mr-2" />
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-100/20 border border-green-500 text-green-500 px-4 py-3 rounded relative flex items-center" role="alert">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        <div className="space-y-4">
          {/* Avatar + upload */}
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <img
                key={avatarKey}
                src={
                  user.avatar_url && user.avatar_url.trim().length > 0
                    ? `${user.avatar_url}${user.avatar_url.includes('?') ? '&' : '?'}refresh=${avatarKey}`
                    : '/default-persona-avatar.svg'
                }
                alt={user.name || user.email}
                className="h-16 w-16 rounded-full object-cover bg-muted"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/default-persona-avatar.svg';
                }}
              />
            </div>
            <div className="flex flex-col space-y-2">
              <label className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 cursor-pointer dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700">
                <span>
                  {isUploadingAvatar
                    ? t('userProfile.uploadingAvatar')
                    : t('userProfile.changeAvatar')}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  disabled={isUploadingAvatar}
                  className="hidden"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    if (!file.type.startsWith('image/')) {
                      setError(t('userProfile.invalidImage'));
                      event.target.value = '';
                      return;
                    }
                    try {
                      setError(null);
                      setSuccess(null);
                      setIsUploadingAvatar(true);
                      const updatedUser = await uploadUserAvatar(file);
                      updateUserInStore(updatedUser);
                      setAvatarKey(Date.now());
                      setSuccess(t('userProfile.avatarSuccess'));
                    } catch (err: unknown) {
                      console.error('Erreur lors du téléchargement de la photo de profil:', err);
                      const errorMessage = err instanceof Error ? err.message : t('global.somethingWentWrong');
                      setError(errorMessage);
                    } finally {
                      setIsUploadingAvatar(false);
                      event.target.value = '';
                    }
                  }}
                />
              </label>
              <p className="text-xs text-muted-foreground">
                {t('userProfile.avatarHelp')}
              </p>
            </div>
          </div>

          {/* Name field */}
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-foreground mb-1">
              {t('userProfile.nameLabel')}
            </label>
            <input
              type="text"
              id="profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('userProfile.namePlaceholder')}
              className={inputClasses}
              disabled={isLoading}
            />
          </div>

          {/* Email field (read-only) */}
          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-foreground mb-1">
              {t('userProfile.emailLabel')}
            </label>
            <input
              type="email"
              id="profile-email"
              value={user.email || ''}
              readOnly
              disabled
              className={`${inputClasses} cursor-not-allowed opacity-60`}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('userProfile.emailReadOnly')}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('userProfile.roleLabel')}{' '}
              <span className="font-medium text-foreground">{user.role || t('global.notAvailable')}</span>
            </p>
          </div>
        </div>

        <div className="mt-6">
          <Button
            type="button"
            onClick={handleSaveChanges}
            disabled={isLoading || name === (user?.name || '')}
          >
            {isLoading ? t('global.saving') : t('userProfile.saveButton')}
          </Button>
        </div>
      </div>


    </div>
  );
};

export default UserProfile;
